import { useState } from "react";
import { Button, Dialog } from "@mui/material";
import PleaseWaitPopup from "../../PleaseWaitPopup";
import PhoneNumberInput from "../../../components/common/PhoneNumberInput";
import EmailAlreadyExistChecking from "../../../components/EmailAlreadyExistChecking";
import { getCustomerEmailChecking } from "../../../services/EmailChecking";
import { useTheme } from "../../../components/hooks/useTheme";
import { post_Customer } from "../../../services/customer";
import colorConfigs from "../../../configs/colorConfigs";
import useAuth from "../../../components/hooks/useAuth";
import Cookies from "js-cookie";
import useTenant from "../../../components/hooks/useTenant";

type AddNewCustomerProps = {
    showPopup: boolean;
    setShowPopup: (value: boolean) => void;
    setSelectedOption: any;
    setContatctId: (value: string) => void;
}

const AddNewCustomer = ({ showPopup, setShowPopup, setSelectedOption, setContatctId }: AddNewCustomerProps) => {
    const tenantId = Cookies.get("tenantId-jj-rental-ui") || "";
    const { auth }: any = useAuth();
    const { handleTenantError }: any = useTenant();
    const { theme, themeStyles } = useTheme();
    const [firstName, setFirstName] = useState<string>("");
    const [lastName, setLastName] = useState<string>("");
    const [emailAddress, setEmailAddress] = useState<string>("");
    const [phoneNumber, setPhoneNumber] = useState<string>("");
    const [emailTouched, setEmailTouched] = useState<boolean>(false);
    const [pleaseWait, setPleaseWait] = useState<boolean>(false);
    const [emptyFieldAlert, setEmptyFieldAlert] = useState<boolean>(false);
    const closeIconFill = theme === "dark" ? "fill-[#E4E7EC]" : "fill-[#1D2939]";

    const handleSubmit = async () => {
        if (firstName && lastName && emailAddress && phoneNumber) {
            setEmptyFieldAlert(false);
            setPleaseWait(true);

            await post_Customer({
                firstName,
                lastName,
                emailAddress,
                phoneNumber,
            }, auth?.id, tenantId)
                .then((res) => {
                    setPleaseWait(false);
                    setSelectedOption({
                        value: res?.data?.id,
                        label: res?.data?.firstName + " " + res?.data?.lastName
                    })
                    setContatctId(res?.data?.id.toString());
                    setShowPopup(false);
                    handleTenantError(null);
                })
                .catch((error) => {
                    setPleaseWait(false);
                    handleTenantError(error);
                });
        } else {
            setEmptyFieldAlert(true);
        }
    }

    return (
        <>
            <Dialog
                open={showPopup}
                maxWidth="md"
                fullScreen={false}
                sx={{
                    '& .MuiDialog-paper': {
                        maxWidth: '700px',
                        width: '100%',
                        borderRadius: '16px',
                    },
                }}
            >
                <div className={`px-[15px] py-[15px] h-full ${themeStyles.main.tailwindSectionBg}`}>
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 320.591 320.591"
                        className={`w-3 cursor-pointer float-right ${closeIconFill}`}
                        onClick={() => setShowPopup(false)}
                    >
                        <path d="M30.391 318.583a30.37 30.37 0 0 1-21.56-7.288c-11.774-11.844-11.774-30.973 0-42.817L266.643 10.665c12.246-11.459 31.462-10.822 42.921 1.424 10.362 11.074 10.966 28.095 1.414 39.875L51.647 311.295a30.366 30.366 0 0 1-21.256 7.288z" />
                        <path d="M287.9 318.583a30.37 30.37 0 0 1-21.257-8.806L8.83 51.963C-2.078 39.225-.595 20.055 12.143 9.146c11.369-9.736 28.136-9.736 39.504 0l259.331 257.813c12.243 11.462 12.876 30.679 1.414 42.922-.456.487-.927.958-1.414 1.414a30.368 30.368 0 0 1-23.078 7.288z" />
                    </svg>
                    <div className={`w-full bg-transparent rounded-lg p-[10px] flex flex-col gap-2.51`}>
                        <div className="w-full mt-2">
                            <div className={`w-full`}>
                                <div className="mt-2 p-2">
                                    <div className="w-full">
                                        <div className="flex flex-col gap-y-[0.5rem]">

                                            <div>
                                                <div className={`flex justify-between items-center border-b mb-[0.75rem] ${themeStyles.main.borderColor}`}>
                                                    <div className="flex items-center gap-1.5">
                                                        <h2 className={`text-sm font-semibold ${themeStyles.main.textHeadingColor}`}>Add Customer</h2>
                                                    </div>
                                                </div>

                                                {
                                                    emptyFieldAlert && (
                                                        <span className="text-xs text-red-500 inline-block my-2">All fields are mandatory!</span>
                                                    )
                                                }

                                                <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                                                    <div className="space-y-4">
                                                        <div>
                                                            <label className={`text-xs font-bold ${themeStyles.form.tailwindLabelTextColor}`}>First Name</label>
                                                            <input
                                                                type="text"
                                                                className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                                                value={firstName}
                                                                onChange={(e) => setFirstName(e.target.value)}
                                                            />
                                                        </div>

                                                        <div>
                                                            <EmailAlreadyExistChecking
                                                                emailAddress={emailAddress}
                                                                setEmailAddress={setEmailAddress}
                                                                getEmailChecking={getCustomerEmailChecking}
                                                                className={{
                                                                    label: {
                                                                        fontSize: "12px",
                                                                        fontWeight: 500,
                                                                        color: themeStyles.form.labelTextColor
                                                                    },
                                                                    input: {
                                                                        width: "100%",
                                                                        fontSize: "14px",
                                                                        borderRadius: "6px",
                                                                        outline: "none",
                                                                        color: themeStyles.form.inputTextColor
                                                                    },
                                                                    successMessage: {
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        marginTop: '0.25rem',
                                                                        fontSize: '0.75rem',
                                                                        color: '#00b300',
                                                                    },
                                                                    errorMessage: {
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        marginTop: '0.25rem',
                                                                        fontSize: '0.75rem',
                                                                        color: '#ff3333',
                                                                    },
                                                                }}
                                                                touched={emailTouched}
                                                                setTouched={setEmailTouched}
                                                            />
                                                        </div>
                                                    </div>

                                                    <div className="space-y-4">
                                                        <div>
                                                            <label className={`text-xs font-bold ${themeStyles.form.tailwindLabelTextColor}`}>Last Name</label>
                                                            <input
                                                                type="text"
                                                                className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                                                value={lastName}
                                                                onChange={(e) => setLastName(e.target.value)}
                                                            />
                                                        </div>

                                                        <div>
                                                            <label className={`text-xs font-bold ${themeStyles.form.tailwindLabelTextColor}`}>Phone Number</label>
                                                            <PhoneNumberInput
                                                                name={"phoneNumber"}
                                                                value={phoneNumber}
                                                                setValue={setPhoneNumber}
                                                                fontSize={"14px"}
                                                                paddingBlock={"5px"}
                                                                required={true}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="flex items-center gap-4 mt-6">
                                                <Button variant="contained"
                                                    sx={{
                                                        backgroundColor: colorConfigs.btn.main.bg
                                                    }}
                                                    onClick={handleSubmit}
                                                >
                                                    Save
                                                </Button>

                                                <Button
                                                    type="button"
                                                    variant="contained"
                                                    sx={{
                                                        backgroundColor: colorConfigs.btn.cancel.bg,
                                                        border: colorConfigs.btn.cancel.border,
                                                        color: colorConfigs.btn.cancel.color
                                                    }}
                                                    onClick={() => setShowPopup(false)}
                                                >
                                                    Cancel
                                                </Button>
                                            </div>

                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Dialog>

            <PleaseWaitPopup pleaseWaitPopup={pleaseWait} />
        </>
    )
}

export default AddNewCustomer;